import React from 'react'
import {useUsers} from 'hooks/useUsers/useUsers'
import {
    Spinner, 
    Card,
    CardBody,
    Flex,
    Heading,
    Table,
    TableCaption,
    TableContainer,
    Tbody,
    Th,
    Thead,
    Tr, Td, Button,
    VStack, Box, HStack, Text, useBreakpointValue
} from '@chakra-ui/react'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import {UserModal} from './UserAdd/UserModal/UserModal'
import {NavLink} from 'react-router-dom'
import { Filters } from 'components/Filters'

export const Users = () => {
    const {users, fetchUsers, setFilters, filters } = useUsers()

    const [isLoading, setIsLoading] = React.useState(false)
    const isMobile = useBreakpointValue({ base: true, lg: false })
    
    const donwloadExcel = async (data, filename) => {
        setIsLoading(true)
        try {
            const workbook = new ExcelJS.Workbook()
            const worksheet = workbook.addWorksheet('Workers')
            worksheet.columns = [
                { header: 'TZ', key: 'identify', width: 20 },
                { header: 'Name', key: 'name', width: 25 },
                { header: 'Roles', key: 'roles', width: 30 },
                { header: 'Phone', key: 'phone', width: 15 }
            ]
            data.forEach(user => {
                worksheet.addRow({
                    identify: user.identify,
                    name: `${user.name} ${user.last_name}`,
                    roles: [
                        user.is_counter ? 'Counter' : '',
                        user.is_shift_manager ? 'Shift Manager' : '',
                        user.is_counting_manager ? 'Counting Manager' : '',
                        user.is_admin ? 'Admin' : ''
                    ].filter(Boolean).join(', '),
                    phone: user.phone
                })
            })
            const buffer = await workbook.xlsx.writeBuffer()
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
            saveAs(blob, `${filename}.xlsx`)
        } catch (error) {
            console.error('Error downloading Excel:', error)
        } finally {
            setIsLoading(false)
        }
    }

    const getRoles = (user) => (
        [
            user.is_counter ? 'Counter' : '',
            user.is_shift_manager ? 'Shift Manager' : '',
            user.is_counting_manager ? 'Counting Manager' : '',
            user.is_admin ? 'Admin' : ''
        ].filter(Boolean).join(', ')
    )

    return (
        <>
            {!users ? <Spinner/> : 
            <Card m={2}>
                <Flex justifyContent='space-between' alignItems='center'>
                    <Heading m={3} size='md'> Workers </Heading>
                    <UserModal fetchUsers={fetchUsers} mode={'new'}/>
                    <Button 
                        onClick = { () => donwloadExcel(users, 'workers') }
                        colorScheme='teal'
                        size='sm'
                        m={3}
                        isLoading={isLoading}
                        loadingText='Downloading...'
                        >
                        Download
                    </Button>
                </Flex>
                <CardBody>
                    <Filters 
                        extraConfig={[
                            { type: 'identify', placeholder: 'TZ' },
                            { type: 'name', placeholder: 'Name' },
                            { type: 'last_name', placeholder: 'Last Name' },
                            { type: 'phone', placeholder: 'Phone' }
                                    ]}
                                    filters={filters ?? {}}
                                    setFilters={setFilters}
                    />
                    {isMobile ? (
                        <VStack spacing={4} align='stretch' mt={4}>
                            {users.map(({ _id, identify, name, last_name, is_counter, is_shift_manager, is_counting_manager, is_admin, phone, address }) => {
                            const user = {_id, identify, name, last_name, is_counter, is_shift_manager, is_counting_manager, is_admin, phone, address}
                            return (
                            <Box key={_id} borderWidth='1px' borderRadius='12px' p={4}>
                                        <Text fontWeight='600' fontSize='md' mb={2}>
                                            {name} {last_name}
                                        </Text>
                                        <VStack align='stretch' spacing={1} borderTopWidth='1px' pt={2} fontSize='sm'>
                                            <HStack justify='space-between'>
                                                <Text color='gray.500'>TZ</Text>
                                                <Text>{identify}</Text>
                                            </HStack>
                                            <HStack justify='space-between'>
                                                <Text color='gray.500'>Roles</Text>
                                                <Text textAlign='right'>{getRoles(user)}</Text>
                                            </HStack>
                                            <HStack justify='space-between'>
                                                <Text color='gray.500'>Phone</Text>
                                                <Text>{phone}</Text>
                                            </HStack>
                                        </VStack>
                                        <HStack mt={3} justify='flex-end' spacing={2}>
                                            <UserModal
                                                mode='edit'
                                                user={user}
                                                fetchUsers={fetchUsers}
                                            />
                                            <Button size={'sm'} as={NavLink} to={`/working-hours/user/${_id}`} colorScheme='yellow'>
                                                Hours
                                            </Button>
                                        </HStack>
                                    </Box>
                                )
                            })}
                        </VStack>
                    ) : (
                    <TableContainer>
                        <Table variant='striped'>
                            <TableCaption>List of the Countings</TableCaption>
                            <Thead>
                                <Tr>
                                    <Th>TZ</Th>
                                    <Th>Name</Th>
                                    <Th>Roles</Th>
                                    <Th>Phone</Th>
                                    <Th>Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {users.map(({ _id, identify, name, last_name, is_counter, is_shift_manager, is_counting_manager, is_admin, phone, address }) =>(
                                    <Tr  key={_id}> 
                                        <Td>
                                            {identify}
                                        </Td>
                                        <Td>
                                            {name} {last_name}
                                        </Td>
                                        <Td>
                                            {getRoles({ is_counter, is_shift_manager, is_counting_manager, is_admin })}
                                        </Td>
                                        <Td>
                                            { phone }
                                        </Td>
                                        <Td>
                                            <UserModal
                                                mode='edit'
                                                user={{ _id, identify, name, last_name, is_counter, is_shift_manager, is_counting_manager, is_admin, phone, address }}
                                                fetchUsers={fetchUsers}
                                            />
                                            <Button size={'sm'} mr={2} as={NavLink} to={`/working-hours/user/${_id}`} colorScheme='yellow'>Hours</Button>
                                        </Td>
                                    </Tr>
                                    ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                    )}
                </CardBody>
            </Card>
            
            }
        </>
    )
}
